import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addRealTimeNotification } from 'store/notificationsSlice';
import useSharedWebSocket from './useSharedWebSocket';
/**
 * Hook to subscribe to real-time notifications via shared WebSocket
 * Automatically adds new notifications to the Redux store
 */
export const useSharedNotificationSubscription = () => {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.user.data);
    const { subscribe, unsubscribe, sendMessage: sendWebSocketMessage, isConnected, } = useSharedWebSocket();
    const subscriptionSentRef = useRef(false);
    const isSubscribedRef = useRef(false);
    const subscriberId = useRef('notification-subscription');
    // Subscribe to shared WebSocket
    useEffect(() => {
        // Prevent multiple subscriptions
        if (isSubscribedRef.current) {
            return undefined;
        }
        isSubscribedRef.current = true;
        const handleMessage = (data) => {
            if (data.type === 'connection_ack') {
                // Subscribe to notifications for this user (only once)
                if (!subscriptionSentRef.current) {
                    // Add a small delay to ensure the connection is fully established
                    setTimeout(() => {
                        sendWebSocketMessage({
                            type: 'subscribe_notifications',
                            id: `notif_sub_${Date.now()}`,
                            payload: {
                                userId: currentUser === null || currentUser === void 0 ? void 0 : currentUser.id,
                            },
                        });
                        subscriptionSentRef.current = true;
                    }, 100);
                }
                return;
            }
            if (data.type === 'notification_subscription_ack') {
                return;
            }
            if (data.type === 'new_notification' && data.payload) {
                const { notification } = data.payload;
                // Add the notification to the Redux store
                dispatch(addRealTimeNotification(notification));
            }
        };
        const handleConnectionChange = (connected) => {
            if (connected) {
                // Always re-subscribe when connection is established (including reconnections)
                // Reset the flag first to allow re-subscription
                subscriptionSentRef.current = false;
                // Send subscription immediately when connected
                setTimeout(() => {
                    if (!subscriptionSentRef.current) {
                        sendWebSocketMessage({
                            type: 'subscribe_notifications',
                            id: `notif_sub_${Date.now()}`,
                            payload: {
                                userId: currentUser === null || currentUser === void 0 ? void 0 : currentUser.id,
                            },
                        });
                        subscriptionSentRef.current = true;
                    }
                }, 100);
            }
        };
        const currentSubscriberId = subscriberId.current;
        subscribe({
            id: currentSubscriberId,
            onMessage: handleMessage,
            onConnectionChange: handleConnectionChange,
        });
        return () => {
            isSubscribedRef.current = false;
            unsubscribe(currentSubscriberId);
        };
    }, [dispatch, subscribe, unsubscribe, sendWebSocketMessage, currentUser]);
    return {
        isConnected,
        hasWebSocket: Boolean(currentUser === null || currentUser === void 0 ? void 0 : currentUser.id),
    };
};
export default useSharedNotificationSubscription;
